export type LOADING_STATE = "pending" | "fulfilled" | "rejected";
export const TOAST_TIMEOUT = 10000;
export const TOAST_WITH_LINK_TIMEOUT = 20000;
export const AUTO_REFRESH_INTERVAL_TIME = 300000; // Five minutes
export const MOBILE_BREAKPOINT = "600px";
export const CLIENT_ID_HFE = "HFE";
export const ERROR_ASSESSMENT_CUTOFF_DAYS = 30;
export const SECONDS_IN_ONE_DAY = 86400;

export const CREATE_ASSESSMENT_COMMENT_CHARACTER_LIMIT = 1000;
export const CREATE_ASSESSMENT_COMMENT_ERROR_MESSAGE = `Comments cannot exceed ${CREATE_ASSESSMENT_COMMENT_CHARACTER_LIMIT} characters`;
export const INITIAL_AND_FOLLOWUP_VALUES = "initialAndFollowUpValues";

export const TOTAL_TIME_SPENT_ON_ALL_PAGE = "TOTAL_TIME_SPENT_ON_ALL_PAGE";
export const AMOUNT_OF_TIME_IN_SECOND = "AMOUNT_OF_TIME_IN_SECOND";

export const TIME_ON_ADDITIONAL_INFO_PAGE = "TIME_ON_ADDITIONAL_INFO_PAGE";
export const ADDITIONAL_INFO_TIME_IN_SECOND = "ADDITIONAL_INFO_TIME_IN_SECOND";

export const UPLOADED_VS_RECORDED_VIDEO_SUBMISSION =
  "UPLOADED_VS_RECORDED_VIDEO_SUBMISSION";
export const RECORD = "RECORD";
export const UPLOAD = "UPLOAD";
export const UPLOADED_VS_RECORDED = "UPLOADED_VS_RECORDED";

export const FE_COUNTRY_LIST = ["AUS", "JPN", "IND", "SGP"];

export const NA_COUNTRY_LIST = ["USA", "CAN", "MEX", "BRA", "CRI"];

export const EU_COUNTRY_LIST = [
  "ZAF",
  "AUT",
  "BEL",
  "TUR",
  "CZE",
  "SWE",
  "DNK",
  "ESP",
  "FRA",
  "IRL",
  "ITA",
  "LUX",
  "NLD",
  "POL",
  "SVK",
  "GBR",
  "ARE",
  "SAU",
  "EGY",
  "ISR",
  "ROU",
];

export const FE_COUNTRY_LIST_WITH_CODE_AND_NAME = [
  "AU (Australia)",
  "IN (India)",
  "JP (Japan)",
  "SG (Singapore)",
];
export const NA_COUNTRY_LIST_WITH_CODE_AND_NAME = [
  "BR (Brazil)",
  "CA (Canada)",
  "CR (Costa Rica)",
  "MX (Mexico)",
  "US (United States)",
];
export const EU_COUNTRY_LIST_WITH_CODE_AND_NAME = [
  "AE (United Arab Emirates)",
  "AT (Austria)",
  "BE (Belgium)",
  "CZ (Czech Republic)",
  "DK (Denmark)",
  "EG (Egypt)",
  "ES (Spain)",
  "FR (France)",
  "IE (Ireland)",
  "IL (Israel)",
  "IT (Italy)",
  "LU (Luxembourg)",
  "MP (Northern Mariana Islands)",
  "NL (Netherlands)",
  "PL (Poland)",
  "RO (Romania)",
  "SA (Saudi Arabia)",
  "SE (Sweden)",
  "SK (Slovakia)",
  "TR (Turkey)",
  "UK (United Kingdom)",
  "ZA (South Africa)",
];

export const SUPERNOVA_DOMAIN = "ergona.whs.amazon.dev";
export const ONE_VIDEO_FRAME_LENGTH_IN_SECONDS = 0.2;
export const INITIAL_ASSESSMENT = "Initial Assessment";
export const FOLLOW_UP_ASSESSMENT = "Follow-Up Assessment";
export const FOLLOWUP = "followup";
export const INITIAL = "initial";
export const RED_DOT_WIDTH = 15;
export const TECHNICAL_RISK_ASSESSMENT = "Technical Risk Assessment";
export const JOB_ROTATION_ASSESSMENT = "Job Rotation Assessment";
export const CREATE_NEW_ASSESSMENT = "Create a new assessment";
export const VIEW_ASSESSMENT_LIBRARY = "View assessment library";
export const ERGONA_SME_USER = "ergona-superuser";
export const TRA_TYPE_RULA = "RULA";
export const TRA_TYPE_REBA = "REBA";
export const TRA_TYPE_LME = "LME";

export const LME_ACTIVITY_TYPES = {
  LIFT: "LIFT",
  LOWER: "LOWER",
  PUSH: "PUSH",
  PULL: "PULL",
  CARRY: "CARRY",
} as const;

export type LmeActivityType =
  (typeof LME_ACTIVITY_TYPES)[keyof typeof LME_ACTIVITY_TYPES];

export const LME_TASK_DISPLAY_LABEL: Record<string, string> = {
  [LME_ACTIVITY_TYPES.LIFT]: "Lift",
  [LME_ACTIVITY_TYPES.LOWER]: "Lower",
  [LME_ACTIVITY_TYPES.PUSH]: "Push",
  [LME_ACTIVITY_TYPES.PULL]: "Pull",
  [LME_ACTIVITY_TYPES.CARRY]: "Carry",
};

// Lift and Lower share one form/UI (lmeLiftLowerForm.tsx); Push and Pull
// share another (lmePushPullForm.tsx). Only the calculation differs per
// specific type - use these helpers instead of repeating the OR checks.
export const isLiftOrLower = (type: LmeActivityType | ""): boolean =>
  type === LME_ACTIVITY_TYPES.LIFT || type === LME_ACTIVITY_TYPES.LOWER;

export const isPushOrPull = (type: LmeActivityType | ""): boolean =>
  type === LME_ACTIVITY_TYPES.PUSH || type === LME_ACTIVITY_TYPES.PULL;

export const LME_ASSESSMENT_TYPE_DESCRIPTION =
  "Liberty Mutual Equations (previously known as SNOOK Tables) are ergonomic assessment tools designed to assess the maximum acceptable load, the force that an individual perceives they safely generate to execute a lift/lower, push/pull, or carry activity.";

// Structured (rather than a flat string array) so ScoreInfoPopover can lay
// out a REBA/RULA example score + description, then an LME example pill +
// description, matching the confirmed richer popover design - the plain
// string-list content (still used by CustomPopoverWithIcon elsewhere) can't
// express that layout.
export type ScoreHeaderPopoverContent = {
  rebaRulaHeading: string;
  rebaRulaDescription: string[];
  lmeHeading: string;
  lmeDescription: string[];
};

// Same explanatory copy applies regardless of whether the column is
// "Initial Score" or "Follow-Up Score" - the popover explains what the
// score means/how to read it, not which column it's attached to (the
// column header text itself already conveys that).
const SCORE_HEADER_POPOVER_CONTENT: ScoreHeaderPopoverContent = {
  rebaRulaHeading: "This score is used by REBA and RULA assessments.",
  rebaRulaDescription: [
    "REBA score range: 1–15.",
    "RULA score range: 1–7.",
    "The higher the number, the greater the ergonomic risk.",
  ],
  lmeHeading:
    "This score is used by Liberty Mutual Equations (LME) assessments.",
  lmeDescription: [
    "It shows the percentage female population who can safely perform the task for a full shift.",
    "75% or above is green: the load is acceptable.",
    "Below 75% is red: reduce the load to get back above 75%.",
  ],
};

export const SCORE_HEADER_POPOVER_CONTENT_BY_HEADER: Record<
  string,
  ScoreHeaderPopoverContent
> = {
  "Initial Score": SCORE_HEADER_POPOVER_CONTENT,
  "Follow-Up Score": SCORE_HEADER_POPOVER_CONTENT,
};

export const LME_CAPABLE_POPULATION_THRESHOLD = 75;

export const LME_UNIT_SYSTEM = {
  METRIC: "METRIC",
  IMPERIAL: "IMPERIAL",
} as const;

export type LmeUnitSystem =
  (typeof LME_UNIT_SYSTEM)[keyof typeof LME_UNIT_SYSTEM];

// Selectable frequency units. The three LME forms build their dropdown from
// Object.values() of this object, so removing an entry retires it from the
// picker everywhere at once. "actions_per_day" was retired here, but is
// still handled on the read path (see the DISPLAY_LABEL / ERROR_PHRASE maps
// below and toFrequencyPerMinute in lmeValidationService) so assessments
// already saved with it keep rendering and validating correctly.
export const LME_FREQUENCY_UNITS = {
  ACTIONS_PER_MIN: "actions_per_min",
  ACTIONS_PER_HOUR: "actions_per_hour",
} as const;

export type LmeFrequencyUnit =
  (typeof LME_FREQUENCY_UNITS)[keyof typeof LME_FREQUENCY_UNITS];

// Keyed by string rather than only by LME_FREQUENCY_UNITS members because
// retired units must still resolve here. Consumers (lmeResultsSummary.tsx,
// lmePrintableRowData.ts) index these maps with the frequencyUnit returned
// by the API and only guard against a falsy value, not an unknown one - a
// missing entry renders "undefined" to the user. Once a unit is dropped
// from LME_FREQUENCY_UNITS the compiler can no longer tell us it is still
// reachable at runtime, so do NOT delete the retired entries below.
export const LME_FREQUENCY_UNIT_DISPLAY_LABEL: Record<string, string> = {
  [LME_FREQUENCY_UNITS.ACTIONS_PER_MIN]: "Action per min",
  [LME_FREQUENCY_UNITS.ACTIONS_PER_HOUR]: "Action per hour",
  // Retired from the picker; read path only.
  actions_per_day: "Action per day",
};

export const LME_FREQUENCY_UNIT_ERROR_PHRASE: Record<string, string> = {
  [LME_FREQUENCY_UNITS.ACTIONS_PER_MIN]: "actions per minute",
  [LME_FREQUENCY_UNITS.ACTIONS_PER_HOUR]: "actions per hour",
  // Retired from the picker; read path only.
  actions_per_day: "actions per day",
};

// Per manager decision (see chat history / BRD_ergona_lme_assessment.md
// "Implementation Notes"), validated ranges follow the Liberty Mutual
// Python reference formulas (lme-reference/*.py) rather than the BRD's
// stated ranges, since the BRD collapsed Carry and Push/Pull into one
// shared range that doesn't match either activity type's actual formula
// validation. Ranges below are split per activity type to reflect this;
// each is annotated with its source Python file.
export const LME_VALIDATION = {
  CARRY: {
    // carry_mal_calculator.py validate_inputs()
    VERTICAL_HAND_HEIGHT_MIN_CM: 71,
    VERTICAL_HAND_HEIGHT_MAX_CM: 103,
    HORIZONTAL_DISTANCE_MIN_M: 2.1,
    HORIZONTAL_DISTANCE_MAX_M: 10,
    FREQUENCY_MIN_PER_MINUTE: 0.0021,
    FREQUENCY_MAX_PER_MINUTE: 10,
    // DH x F <= 29 m/min - not present in BRD at all, only in the formula.
    VELOCITY_MAX_M_PER_MIN: 29,
  },
  PUSH_PULL: {
    // push_pull_mal_calculator.py is_in_validated_range()
    VERTICAL_HAND_HEIGHT_MIN_CM: 58,
    VERTICAL_HAND_HEIGHT_MAX_CM: 133,
    HORIZONTAL_DISTANCE_MIN_M: 2.1,
    HORIZONTAL_DISTANCE_MAX_M: 61,
    FREQUENCY_MIN_PER_MINUTE: 0.0021,
    FREQUENCY_MAX_PER_MINUTE: 10,
    VELOCITY_MAX_M_PER_MIN: 37,
  },
  LIFT_LOWER: {
    // lift_lower_mal_calculator.py calculate_liberty_mutual_mal()
    HORIZONTAL_LOCATION_MIN_CM: 20,
    HORIZONTAL_LOCATION_MAX_CM: 68,
    FREQUENCY_MIN_PER_MINUTE: 0.0021,
    FREQUENCY_MAX_PER_MINUTE: 20,
    HAND_HEIGHT_MIN_DIFF_CM: 25, // DV < 0.25m
    VELOCITY_MAX_M_PER_MIN: 11,
    REACH_MAX_M: 1.96,
  },
} as const;

export const TRA_ERRORS_MAPPINGS = {
  INFERENCE_INTERNAL_ERROR: {
    errorTitle: "Software processing error.",
    errorContent:
      "This video could not be processed due to a technical issue with the software. Please create a new assessment and re-submit your videos.",
  },
  VIDEO_RESOLUTION_ERROR: {
    errorTitle: "Low video resolution.",
    errorContent:
      "Please ensure the video has greater than 512x512 p resolution by checking the video properties prior to upload. Note: video resolution can be reduced during file transfer from mobile device to desktop. Re-record as necessary.",
  },
  VIDEO_DURATION_ERROR: {
    errorTitle: "Invalid video duration.",
    errorContent:
      "Please ensure the video length is between 3 sec - 5 min. This video should only capture the “activity” specified in the assessment details. For videos capturing multiple “activities”, please separate into individual activity videos and create separate assessments named with  the corresponding activity. Re-record video as necessary.",
  },
  FACE_NOT_DETECTED_ERROR: {
    errorTitle: "The person’s face was not detected.",
    errorContent:
      "Please ensure the person being recorded is fully visible and facing the camera at the start of the video. We recommend having the person face the camera and hold a neutral body position for 1 - 2 seconds before continuing through the activity. Submit a new video when ready.",
  },
  UPLOAD_ERROR: {
    errorTitle: "Video upload error.",
    errorContent:
      "Please resubmit this video. After submitting the assessment, do not exit or refresh the web page until you receive a message saying the video has uploaded successfully.",
  },
  PERSON_MISSING_ERROR: {
    errorTitle:
      "The person being recorded was not fully in view for the entire video.",
    errorContent:
      "Please ensure the person’s entire body remains visible throughout the recording and the person is not partially or fully hidden behind any objects for any amount of time. Re-record as necessary.",
  },
  LOW_FPS_ERROR: {
    errorTitle: "Low frames per second.",
    errorContent:
      "Please ensure your camera is set to record using 10 frames per second (FPS) or greater. This is a device setting and ensures the video quality meets minimum requirements for processing. Re-record your video and submit when ready.",
  },
  VIDEO_TRANSFORMATION_ERROR: {
    errorTitle: "Software processing error.",
    errorContent:
      "This video could not be processed due to a technical issue with the software. Please create a new assessment and re-submit your videos.",
  },
  UNREADABLE_VIDEO_ERROR: {
    errorTitle: "Video upload error.",
    errorContent:
      "Please resubmit this video. After submitting the assessment, do not exit or refresh the web page until you receive a message saying the video has uploaded successfully.",
  },
  VIDEO_QUALITY_ERROR: {
    errorTitle: "Video quality error.",
    errorContent: "",
  },
  NONE: {
    errorTitle: "",
    errorContent: "",
  },
};
export const TRA_CREATE_BREAD_CRUMBS = [
  {
    label: "Homepage",
    url: "/home",
  },
  {
    label: "Create TRA",
    url: "",
  },
];
export const popoverMessagesForRula = {
  ARM_POSTURE: [
    "Ergona will automatically detect most positions and angles of the upper and lower arms. Only select the optional inputs below if you see the specific movements within the activity.",
  ],
  WRIST_POSTURE: [
    "Ergona is not currently able to automatically detect wrist posture.",
    "(1) Determine the worst-case posture and twisting for both wrists and select the corresponding required inputs.",
    "(2) If either wrist is bent from the midline, select the optional input.",
  ],
  ARM_WRIST_MUSCLE_USE_FORCE: [
    "i. Determine if postures of the Arm and Wrist are static (sustained for more than 1 minute at a time), or repeating more than 4 times in a minute. Ensure you are observing the Associate’s movements for several work cycles.",
    "ii. Use a scale to determine the weights of objects, or consult Operations for standard weights of various loads/packages. If force is applied by the Arm or Wrist, rather than a simple lift or lower activity, use a force gauge to measure it.",
  ],
  NECK_TRUNK_POSTURE: [
    "Ergona will automatically detect most positions and angles of the trunk and neck. Only select the optional inputs below if you see the specific twisting movements within the activity.",
  ],
  LEG_POSTURE: [
    "Note whether the legs and feet are",
    "(1) well supported and evenly balanced, or",
    "(2) not well supported or evenly balanced",
  ],
  NECK_TRUNK_LEG_MUSCLE_USE_FORCE: [
    "i. Determine if postures of the Neck, Trunk, and/or Leg are static (sustained for more than 1 minute at a time), or repeating more than 4 times in a minute. Ensure you are observing the Associate’s movements for several work cycles.",
    "ii. Use a scale to determine the weights of objects, or consult Operations for standard weights of various loads/packages. If force is applied by the Neck, Trunk, and/or Leg, rather than a simple lift or lower activity, use a force gauge to measure it.",
  ],
};
export const popoverMessageForReba = {
  FORCE_LOAD_COUPLING: [
    "Select the appropriate Force/Load and Coupling input based on the weight of items being handled and the hand position required for grasping the items. For Activity Score, select all options that apply OR choose “Not applicable”.",
  ],
  ARM_POSTURE: [
    "Ergona will automatically detect most positions and angles of the upper and lower arms. Only select the optional inputs below if you see the specific movements within the activity.",
  ],
  WRIST_POSTURE: [
    "Ergona is not currently able to automatically detect wrist posture.",
    "(1) Determine the worst-case posture for both wrists and select the corresponding required inputs.",
    "(2) If either wrist is bent from the midline in that same worst-case posture, select the optional input.",
  ],
  NECK_TRUNK_POSTURE: [
    "Ergona will automatically detect most positions and angles of the trunk and neck. Only select the optional inputs below if you see the specific twisting movements within the activity.",
  ],
};
export const initialHighestRebaScores = {
  time_stamp: "timeStamp",
  REBA_score: "rebaScore",
  neck_score: "neckScore",
  trunk_score: "trunkScore",
  leg_score: "legScore",
  upperArm_score: "upperArmScore",
  lowerArm_score: "lowerArmScore",
  wrist_score: "wristScore",
  loading: "loading",
  coupling: "coupling",
};
export const initialHighestRulaScores = {
  timeStamp: "",
  rulaScore: "",
  neckScore: "",
  trunkScore: "",
  legScore: "",
  upperArmScore: "",
  lowerArmScore: "",
  wristScore: "",
};
export const createTraProgressSteps = {
  RECORD_AND_UPLOAD_STEP: 0,
  SELECT_TRA_TYPE_STEP: 1,
  ADDITIONAL_INFO_STEP: 2,
};

export const createTraProgressStepsV2 = {
  CHOOSE_ASSESSMENT: 0,
  ADD_INFO: 1,
  ASSESSMENT: 2,
  REVIEW_AND_SUBMIT: 3,
};

export const TRA_STEPPER_LABELS_V2 = [
  "Choose Assessment",
  "Add Info",
  "Assessment",
  "Review and Submit",
];

export const ergonaExternalLinks = {
  ERGONA_RIVER_TECHNICAL_ISSUE:
    "https://river.amazon.com/VCO/workflows?buildingType=PI3%20-%20WW&q0=0602a648-8cde-4982-bd1f-fddfc032798d&q1=e5ac4c17-950a-4b3e-b192-a5f937e86508&id=e5ac4c17-950a-4b3e-b192-a5f937e86508",
};
export const traToastMessages = {
  CREATE_ASSESSMENT_SUCCESS_1:
    "Please do NOT close/refresh tab until video uploading is complete.",
  CREATE_ASSESSMENT_SUCCESS_2:
    "You will be notified via email when assessment results are available.",
  CREATE_ASSESSMENT_INFO_FOR_AUSTIN: `You are creating a TRA to be linked with an AUSTIN GRA. After you submit the new assessment in Ergona, copy the URL link for the "Assessment Details" page and paste the hyperlink back in AUSTIN. If you do not paste the hyperlink, your Ergona assessment will not be tracked in the AUSTIN GRA.`,
};
